<template>
  <div class="business-page">
    <section class="list-header">
      <div class="title-row">
        <v-btn icon class="back-button" @click="$router.push('/home')">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <h1>Comércio local</h1>
          <p>Benefícios e promoções para moradores</p>
        </div>
      </div>
      <div class="header-actions">
        <v-btn icon class="refresh-button" title="Validar cupom" @click="openValidation">
          <v-icon>mdi-qrcode-scan</v-icon>
        </v-btn>
        <v-btn icon class="refresh-button" @click="load">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </section>

    <section v-if="featured.length" class="featured-strip">
      <button v-for="item in featured" :key="item.id" type="button" class="featured-card" @click="openBusinessDetails(item)">
        <span>Destaque</span>
        <strong>{{ item.name }}</strong>
        <p>{{ item.promotion_title || item.description }}</p>
      </button>
    </section>

    <section v-if="!items.length && !loading" class="empty-panel">
      <v-icon color="primary" size="36">mdi-storefront-outline</v-icon>
      <strong>Nenhuma promoção ativa</strong>
      <span>Os comércios parceiros aparecerão aqui.</span>
    </section>

    <section v-if="pendingCoupons.length" class="coupon-wallet">
      <div class="section-title">
        <span>Cupons resgatados</span>
        <strong>Apresente o QR Code no comércio</strong>
      </div>
      <button
        v-for="coupon in pendingCoupons"
        :key="coupon.id"
        type="button"
        class="wallet-card"
        @click="showRedemption(coupon)"
      >
        <span>
          <strong>{{ coupon.localBusiness ? coupon.localBusiness.name : "Comércio local" }}</strong>
          <small>{{ coupon.coupon_code }}</small>
        </span>
        <v-chip x-small color="secondary" text-color="primary">Pendente</v-chip>
      </button>
    </section>

    <article v-for="item in items" :key="item.id" class="business-card" role="button" tabindex="0" @click="openBusinessDetails(item)" @keydown.enter.prevent="openBusinessDetails(item)">
      <div class="business-avatar">
        <v-icon color="primary">mdi-storefront-outline</v-icon>
      </div>
      <div class="business-body">
        <div class="business-head">
          <div>
            <strong>{{ item.name }}</strong>
            <span>{{ categoryLabel(item.category) }}</span>
          </div>
          <v-chip v-if="item.is_featured" x-small color="secondary" text-color="primary">Destaque</v-chip>
        </div>
        <h2 v-if="item.promotion_title">{{ item.promotion_title }}</h2>
        <p>{{ item.promotion_description || item.description || "Comércio parceiro da sua região." }}</p>
        <div class="coupon-row" v-if="item.coupon_code">
          <small>Cupom</small>
          <strong>{{ item.coupon_code }}</strong>
        </div>
        <div class="actions-row">
          <button v-if="item.coupon_code" type="button" @click.stop="redeemCoupon(item)">
            <v-icon small>mdi-ticket-percent-outline</v-icon>
            Resgatar cupom
          </button>
          <a v-if="item.whatsapp" :href="whatsappLink(item)" target="_blank" rel="noopener" @click.stop>
            <v-icon small>mdi-whatsapp</v-icon>
            WhatsApp
          </a>
          <a v-if="item.website_url" :href="item.website_url" target="_blank" rel="noopener" @click.stop>
            <v-icon small>mdi-open-in-new</v-icon>
            Abrir
          </a>
        </div>
      </div>
    </article>

    <v-dialog v-model="businessDialog" max-width="460">
      <v-card v-if="selectedBusiness" class="business-detail-dialog">
        <v-card-text>
          <div class="dialog-handle"></div>
          <div class="business-detail-hero">
            <span class="business-detail-icon">
              <v-icon>mdi-storefront-outline</v-icon>
            </span>
            <div>
              <small>{{ categoryLabel(selectedBusiness.category) }}</small>
              <h2>{{ selectedBusiness.name }}</h2>
              <v-chip v-if="selectedBusiness.is_featured" x-small color="secondary" text-color="primary">Destaque</v-chip>
            </div>
          </div>

          <section class="detail-section">
            <span>Sobre o comércio</span>
            <p>{{ selectedBusiness.description || "Comércio parceiro do seu local." }}</p>
          </section>

          <section v-if="selectedBusiness.promotion_title || selectedBusiness.promotion_description || selectedBusiness.coupon_code" class="detail-section promotion-detail">
            <span>Promoção</span>
            <strong v-if="selectedBusiness.promotion_title">{{ selectedBusiness.promotion_title }}</strong>
            <p v-if="selectedBusiness.promotion_description">{{ selectedBusiness.promotion_description }}</p>
            <div v-if="selectedBusiness.coupon_code" class="detail-coupon">
              <small>Cupom</small>
              <strong>{{ selectedBusiness.coupon_code }}</strong>
            </div>
          </section>

          <section v-if="selectedBusiness.address || selectedBusiness.whatsapp || selectedBusiness.website_url" class="detail-section">
            <span>Informações</span>
            <div class="detail-info-list">
              <p v-if="selectedBusiness.address">
                <v-icon size="17">mdi-map-marker-outline</v-icon>
                {{ selectedBusiness.address }}
              </p>
              <p v-if="selectedBusiness.whatsapp">
                <v-icon size="17">mdi-whatsapp</v-icon>
                {{ phoneLabel(selectedBusiness.whatsapp) }}
              </p>
              <p v-if="selectedBusiness.website_url">
                <v-icon size="17">mdi-web</v-icon>
                Site disponível
              </p>
            </div>
          </section>

          <div class="detail-actions">
            <button v-if="selectedBusiness.coupon_code" type="button" @click="redeemSelectedBusiness">
              <v-icon small>mdi-ticket-percent-outline</v-icon>
              Resgatar cupom
            </button>
            <a v-if="selectedBusiness.whatsapp" :href="whatsappLink(selectedBusiness)" target="_blank" rel="noopener">
              <v-icon small>mdi-whatsapp</v-icon>
              WhatsApp
            </a>
            <a v-if="selectedBusiness.website_url" :href="selectedBusiness.website_url" target="_blank" rel="noopener">
              <v-icon small>mdi-open-in-new</v-icon>
              Abrir site
            </a>
          </div>

          <v-btn block text class="mt-3" @click="businessDialog = false">Fechar</v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="redemptionDialog" max-width="380">
      <v-card class="coupon-dialog">
        <v-card-text>
          <div class="dialog-handle"></div>
          <h2>Cupom resgatado</h2>
          <p>Mostre este QR Code no comércio para validar o desconto.</p>
          <div v-if="selectedRedemption" class="qr-box">
            <img :src="qrImageUrl(selectedRedemption)" alt="QR Code do cupom" />
          </div>
          <div v-if="selectedRedemption" class="coupon-token">
            <small>{{ selectedRedemption.localBusiness ? selectedRedemption.localBusiness.name : "Comércio local" }}</small>
            <strong>{{ selectedRedemption.coupon_code }}</strong>
            <span>Válido até {{ formatDate(selectedRedemption.expires_at) }}</span>
            <code>{{ qrPayload(selectedRedemption) }}</code>
          </div>
          <v-btn block depressed color="primary" class="mt-4" @click="redemptionDialog = false">
            Fechar
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="validationDialog" max-width="420" persistent>
      <v-card class="coupon-dialog validation-dialog">
        <v-card-text>
          <div class="dialog-handle"></div>
          <div class="validation-head">
            <div>
              <h2>Validar cupom</h2>
              <p>Aponte a câmera para o QR Code apresentado pelo morador.</p>
            </div>
            <v-btn icon @click="closeValidation">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <div class="scanner-box">
            <video ref="scannerVideo" autoplay muted playsinline></video>
            <span v-if="!scannerActive">{{ scannerMessage }}</span>
          </div>
          <v-text-field
            v-model="manualToken"
            label="Código do QR Code"
            outlined
            dense
            hide-details="auto"
            class="mt-3"
          />
          <v-btn block depressed color="primary" class="mt-3" :loading="validating" @click="validateCoupon(manualToken)">
            Validar desconto
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "BoaruaLocalBusinesses",
  data() {
    return {
      loading: false,
      items: [],
      redemptions: [],
      businessDialog: false,
      redemptionDialog: false,
      validationDialog: false,
      selectedBusiness: null,
      selectedRedemption: null,
      manualToken: "",
      validating: false,
      scannerActive: false,
      scannerMessage: "Camera aguardando inicializacao",
      scannerStream: null,
      scannerTimer: null,
    };
  },
  computed: {
    featured() {
      return this.items.filter((item) => item.is_featured).slice(0, 3);
    },
    pendingCoupons() {
      return this.redemptions.filter((item) => String(item.status) === "pending");
    },
  },
  beforeDestroy() {
    this.stopScanner();
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      const profile = JSON.parse(localStorage.getItem("associationProfile") || "{}");
      if (!profile.tenant_id) return;
      this.loading = true;
      try {
        const response = await axios.get("/local-businesses", {
          params: { tenant_id: profile.tenant_id, limit: 100, search: JSON.stringify({ status: "active" }) },
        });
        this.items = response.data.content.data || response.data.content || [];
        await this.loadCoupons(profile.tenant_id);
      } finally {
        this.loading = false;
      }
    },
    async loadCoupons(tenantId) {
      const response = await axios.get("/local-businesses/coupons/me", {
        params: { tenant_id: tenantId },
      });
      const content = response.data.content;
      this.redemptions = content && content.rows ? content.rows : (content || []);
    },
    openBusinessDetails(item) {
      this.selectedBusiness = item;
      this.businessDialog = true;
    },
    async redeemSelectedBusiness() {
      if (!this.selectedBusiness) return;
      await this.redeemCoupon(this.selectedBusiness);
      this.businessDialog = false;
    },
    async redeemCoupon(item) {
      try {
        const response = await axios.post(`/local-businesses/${item.id}/redeem-coupon`);
        this.selectedRedemption = response.data.content;
        this.redemptionDialog = true;
        await this.load();
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : "Não foi possível resgatar o cupom");
      }
    },
    showRedemption(coupon) {
      this.selectedRedemption = coupon;
      this.redemptionDialog = true;
    },
    qrImageUrl(redemption) {
      const token = encodeURIComponent(this.qrPayload(redemption));
      return `https://api.qrserver.com/v1/create-qr-code/?size=260x260&margin=12&data=${token}`;
    },
    qrPayload(redemption) {
      return `BOARUA_COUPON:${redemption.token}`;
    },
    formatDate(value) {
      if (!value) return "sem validade definida";
      return new Date(value).toLocaleDateString("pt-BR");
    },
    async openValidation() {
      this.validationDialog = true;
      this.manualToken = "";
      await this.$nextTick();
      this.startScanner();
    },
    closeValidation() {
      this.validationDialog = false;
      this.stopScanner();
    },
    async startScanner() {
      this.stopScanner();

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.scannerMessage = "Camera indisponivel neste dispositivo. Informe o codigo manualmente.";
        return;
      }

      if (!("BarcodeDetector" in window)) {
        this.scannerMessage = "Leitura automatica indisponivel. Informe o codigo manualmente.";
        return;
      }

      try {
        this.scannerStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        this.$refs.scannerVideo.srcObject = this.scannerStream;
        const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
        this.scannerActive = true;
        this.scannerMessage = "Camera ativa";
        this.scannerTimer = window.setInterval(async () => {
          if (!this.$refs.scannerVideo || this.validating) return;
          const codes = await detector.detect(this.$refs.scannerVideo).catch(() => []);
          if (codes && codes[0] && codes[0].rawValue) {
            this.manualToken = codes[0].rawValue;
            await this.validateCoupon(codes[0].rawValue);
          }
        }, 900);
      } catch (error) {
        this.scannerActive = false;
        this.scannerMessage = "Nao foi possivel acessar a camera. Informe o codigo manualmente.";
      }
    },
    stopScanner() {
      if (this.scannerTimer) {
        window.clearInterval(this.scannerTimer);
        this.scannerTimer = null;
      }
      if (this.scannerStream) {
        this.scannerStream.getTracks().forEach((track) => track.stop());
        this.scannerStream = null;
      }
      this.scannerActive = false;
    },
    async validateCoupon(value) {
      const token = String(value || "").trim();
      if (!token) {
        this.notifyError("Informe ou leia um QR Code de cupom");
        return;
      }

      this.validating = true;
      try {
        const response = await axios.post("/local-businesses/coupons/validate", { token });
        const redemption = response.data.content;
        this.notifySuccess(`Cupom validado: ${redemption.coupon_code}`);
        this.closeValidation();
        await this.load();
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : "Não foi possível validar o cupom");
      } finally {
        this.validating = false;
      }
    },
    categoryLabel(category) {
      return {
        market: "Mercado",
        pharmacy: "Farmácia",
        restaurant: "Restaurante",
        petshop: "Petshop",
        services: "Serviços",
        construction: "Construção",
        health: "Saúde",
        beauty: "Beleza",
        general: "Comércio local",
      }[category] || category || "Comércio local";
    },
    whatsappLink(item) {
      const phone = String(item.whatsapp || "").replace(/\D/g, "");
      const message = encodeURIComponent(`Olá, vi sua promoção no BOARUA${item.coupon_code ? ` com o cupom ${item.coupon_code}` : ""}.`);
      return `https://wa.me/55${phone}?text=${message}`;
    },
    phoneLabel(value) {
      const digits = String(value || "").replace(/\D/g, "");
      if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
      if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
      return value || "";
    },
    notifySuccess(message) {
      if (this.$dialog && this.$dialog.notify) this.$dialog.notify.success(message, { position: "top-right", timeout: 3000 });
    },
    notifyError(message) {
      if (this.$dialog && this.$dialog.notify) this.$dialog.notify.error(message, { position: "top-right", timeout: 5000 });
    },
  },
};
</script>

<style scoped lang="scss">
.business-page {
  min-height: 100%;
  padding: 0 16px 18px;
  color: #111827;
}

.list-header {
  min-height: 72px;
  margin: 0 -16px 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border-bottom: 1px solid #edf0ed;

  h1,
  p {
    margin: 0;
  }

  h1 {
    font-size: 23px;
  }

  p {
    color: #66736b;
    font-size: 12px;
  }
}

.title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.back-button,
.refresh-button {
  background: #f5f7f5;
  border: 1px solid #dfe7df;
  border-radius: 12px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.featured-strip {
  margin-bottom: 12px;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(230px, 78%);
  gap: 10px;
  overflow-x: auto;
}

.featured-card {
  min-height: 128px;
  padding: 16px;
  display: block;
  color: #ffffff;
  background: linear-gradient(135deg, #057a3d, #003f24);
  border: 0;
  border-radius: 18px;
  text-align: left;

  span {
    color: rgba(255, 255, 255, 0.72);
    font-size: 11px;
    font-weight: 900;
    text-transform: uppercase;
  }

  strong {
    display: block;
    margin-top: 10px;
    font-size: 20px;
  }

  p {
    margin: 7px 0 0;
    color: rgba(255, 255, 255, 0.8);
    font-size: 13px;
  }
}

.coupon-wallet {
  margin-bottom: 12px;
  padding: 14px;
  background: #ffffff;
  border: 1px solid #e1e8e2;
  border-radius: 16px;
}

.section-title {
  margin-bottom: 10px;

  span,
  strong {
    display: block;
  }

  span {
    color: #057a3d;
    font-size: 11px;
    font-weight: 900;
    text-transform: uppercase;
  }

  strong {
    margin-top: 3px;
    color: #10223f;
    font-size: 18px;
  }
}

.wallet-card {
  width: 100%;
  min-height: 62px;
  margin-top: 8px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid #dfe8e3;
  border-radius: 13px;
  background: #f8fbf9;
  color: #10223f;
  text-align: left;

  span,
  strong,
  small {
    display: block;
  }

  small {
    margin-top: 4px;
    color: #057a3d;
    font-weight: 900;
  }
}

.business-card,
.empty-panel {
  background: #ffffff;
  border: 1px solid #e1e8e2;
  border-radius: 16px;
}

.business-card {
  margin-bottom: 10px;
  padding: 14px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;

  &:focus-visible {
    outline: 2px solid #057a3d;
    outline-offset: 2px;
  }

  &:active {
    transform: scale(0.995);
  }
}

.business-avatar {
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eef7f1;
  border-radius: 13px;
}

.business-body {
  min-width: 0;
  flex: 1;
}

.business-head,
.actions-row,
.coupon-row {
  display: flex;
  align-items: center;
}

.business-head {
  justify-content: space-between;
  gap: 8px;

  strong,
  span {
    display: block;
  }

  span {
    color: #66736b;
    font-size: 12px;
  }
}

h2 {
  margin: 10px 0 0;
  font-size: 17px;
}

p {
  margin: 5px 0 0;
  color: #66736b;
  font-size: 13px;
}

.coupon-row {
  width: fit-content;
  margin-top: 10px;
  padding: 8px 10px;
  gap: 10px;
  background: #f4f8f5;
  border-radius: 12px;

  small {
    color: #66736b;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
  }

  strong {
    color: #057a3d;
  }
}

.actions-row {
  margin-top: 12px;
  gap: 8px;
  flex-wrap: wrap;

  a,
  button {
    min-height: 36px;
    padding: 0 10px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #ffffff;
    background: #057a3d;
    border-radius: 11px;
    border: 0;
    font-size: 12px;
    font-weight: 800;
    text-decoration: none;
  }

  button {
    cursor: pointer;
  }
}

.empty-panel {
  min-height: calc(100vh - 184px);
  padding: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  text-align: center;

  span {
    color: #66736b;
    font-size: 13px;
  }
}

.coupon-dialog {
  border-radius: 18px;

  .v-card__text {
    padding: 18px;
  }

  h2,
  p {
    margin: 0;
  }

  h2 {
    color: #10223f;
    font-size: 24px;
  }

  p {
    margin-top: 6px;
    color: #66736b;
    font-size: 14px;
  }
}

.dialog-handle {
  width: 48px;
  height: 5px;
  margin: 0 auto 16px;
  border-radius: 999px;
  background: #cfe2dc;
}

.qr-box {
  width: min(260px, 100%);
  margin: 18px auto 12px;
  padding: 12px;
  border: 1px solid #dfe8e3;
  border-radius: 18px;
  background: #ffffff;

  img {
    width: 100%;
    display: block;
  }
}

.coupon-token {
  padding: 12px;
  border-radius: 14px;
  background: #f4f8f5;
  text-align: center;

  small,
  strong,
  span,
  code {
    display: block;
  }

  small {
    color: #66736b;
    font-size: 12px;
  }

  strong {
    margin-top: 4px;
    color: #057a3d;
    font-size: 22px;
  }

  span {
    margin-top: 4px;
    color: #66736b;
    font-size: 12px;
  }

  code {
    margin-top: 10px;
    padding: 8px;
    overflow-wrap: anywhere;
    border-radius: 10px;
    background: #ffffff;
    color: #10223f;
    font-size: 11px;
  }
}

.validation-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.scanner-box {
  position: relative;
  min-height: 240px;
  margin-top: 14px;
  display: grid;
  place-items: center;
  overflow: hidden;
  border: 1px solid #dfe8e3;
  border-radius: 16px;
  background: #0f172a;
  color: #ffffff;
  text-align: center;

  video {
    width: 100%;
    height: 100%;
    min-height: 240px;
    object-fit: cover;
  }

  span {
    padding: 18px;
    color: rgba(255, 255, 255, 0.82);
    font-size: 13px;
  }
}

.business-detail-dialog {
  border-radius: 20px;

  .v-card__text {
    padding: 18px;
  }
}

.business-detail-hero {
  display: flex;
  align-items: flex-start;
  gap: 13px;

  h2 {
    margin: 3px 0 7px;
    color: #10223f;
    font-size: 24px;
    line-height: 1.08;
  }

  small {
    color: #057a3d;
    font-size: 11px;
    font-weight: 900;
    text-transform: uppercase;
  }
}

.business-detail-icon {
  width: 50px;
  height: 50px;
  flex: 0 0 50px;
  display: grid;
  place-items: center;
  color: #057a3d;
  background: #eef7f1;
  border-radius: 15px;

  .v-icon {
    color: #057a3d;
  }
}

.detail-section {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #edf1ec;

  > span {
    display: block;
    color: #057a3d;
    font-size: 11px;
    font-weight: 900;
    text-transform: uppercase;
  }

  > strong {
    display: block;
    margin-top: 6px;
    color: #10223f;
    font-size: 18px;
    line-height: 1.15;
  }

  p {
    margin-top: 7px;
    color: #66736b;
    font-size: 14px;
    line-height: 1.45;
  }
}

.promotion-detail {
  padding: 14px;
  background: #f8fbf9;
  border: 1px solid #e1e8e2;
  border-radius: 15px;
}

.detail-coupon {
  width: fit-content;
  margin-top: 12px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #e7f6ed;
  border-radius: 12px;

  small {
    color: #66736b;
    font-size: 11px;
    font-weight: 850;
    text-transform: uppercase;
  }

  strong {
    color: #057a3d;
    font-size: 16px;
  }
}

.detail-info-list {
  display: grid;
  gap: 8px;

  p {
    margin: 0;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    color: #10223f;

    .v-icon {
      margin-top: 1px;
      color: #057a3d;
    }
  }
}

.detail-actions {
  margin-top: 16px;
  display: grid;
  gap: 8px;

  a,
  button {
    min-height: 42px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    color: #ffffff;
    background: #057a3d;
    border: 0;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 850;
    text-decoration: none;
  }
}
</style>
